import { forwardRef } from 'react';
import { X } from 'lucide-react';
import { cn } from './cn';

export type ChipVariant =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'info'
  | 'danger'
  | 'outline';
export type ChipSize = 'sm' | 'md';

export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: ChipVariant;
  size?: ChipSize;
  /** Icon rendered before the label. */
  startIcon?: React.ReactNode;
  /** When provided, renders a small delete (X) button at the end. */
  onDelete?: () => void;
}

const base =
  'inline-flex items-center gap-1.5 rounded-lg font-medium whitespace-nowrap select-none ' +
  'transition-colors duration-200';

const variants: Record<ChipVariant, string> = {
  default: 'bg-surface-2 text-content',
  primary: 'bg-primary/12 text-primary',
  success: 'bg-success/12 text-success',
  warning: 'bg-warning/15 text-warning',
  info: 'bg-info/12 text-info',
  danger: 'bg-danger/12 text-danger',
  outline: 'border border-line text-content',
};

const sizes: Record<ChipSize, string> = {
  sm: 'h-6 px-2 text-xs',
  md: 'h-7 px-2.5 text-xs',
};

/**
 * Compact tag / badge-pill — replaces MUI <Chip />.
 * Orange theme for emphasis; `danger` only for genuinely destructive states.
 * Pass `onDelete` to render a removable X button.
 */
export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  ({ variant = 'default', size = 'md', startIcon, onDelete, className, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(base, variants[variant], sizes[size], className)}
      {...props}
    >
      {startIcon && <span className="flex shrink-0 items-center">{startIcon}</span>}
      {children}
      {onDelete && (
        <button
          type="button"
          aria-label="Remove"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className={cn(
            '-mr-0.5 flex shrink-0 items-center justify-center rounded-md p-0.5 text-current/80 ' +
              'transition-colors hover:bg-current/15 hover:text-current ' +
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          )}
        >
          <X size={14} strokeWidth={2.5} />
        </button>
      )}
    </span>
  ),
);

Chip.displayName = 'Chip';
